import React, { useState, useEffect } from 'react';
import {
    Shield,
    Search,
    Calendar,
    User,
    Info,
    AlertTriangle,
    CheckCircle,
    Clock,
    ChevronRight
} from 'lucide-react';

interface AuditLog {
    id: string;
    type: string;
    user_id: string;
    timestamp: string;
    details: string;
}

export const AuditLogManagement: React.FC = () => {
    const [logs, setLogs] = useState<AuditLog[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
    const [limit, setLimit] = useState(100);

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const response = await fetch(`/api/admin/audit-logs?limit=${limit}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            if (!response.ok) throw new Error('Failed to fetch audit logs');
            const data = await response.json();
            setLogs(data);
        } catch (err: any) {
            console.error('Audit Log Error:', err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs();
    }, [limit]);

    const filteredLogs = logs.filter(log =>
        log.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.user_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.details.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const getEventIcon = (type: string) => {
        if (type.includes('fail') || type.includes('error') || type.includes('lockout')) {
            return <AlertTriangle size={16} color="#e74c3c" />;
        }
        if (type.includes('success') || type.includes('create') || type.includes('update')) {
            return <CheckCircle size={16} color="#2ecc71" />;
        }
        return <Info size={16} color="#3498db" />;
    };

    const formatDetails = (detailsStr: string) => {
        try {
            const details = JSON.parse(detailsStr);
            return JSON.stringify(details, null, 2);
        } catch {
            return detailsStr;
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                <div>
                    <h2 style={{ fontSize: 24, fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 12 }}>
                        <Shield size={24} color="var(--accent-primary)" />
                        Audit Logs
                    </h2>
                    <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 4 }}>
                        Monitor system events, security alerts, and administrative actions.
                    </p>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ position: 'relative' }}>
                        <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                        <input
                            type="text"
                            placeholder="Search logs..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                padding: '8px 12px 8px 36px',
                                background: 'var(--bg-secondary)',
                                border: '1px solid var(--border-color)',
                                borderRadius: 8,
                                fontSize: 13,
                                color: 'var(--text-primary)',
                                outline: 'none',
                                width: 240
                            }}
                        />
                    </div>
                    <button
                        onClick={() => fetchLogs()}
                        style={{
                            padding: 8,
                            background: 'var(--bg-secondary)',
                            border: '1px solid var(--border-color)',
                            borderRadius: 8,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'var(--text-muted)',
                            transition: 'all 0.2s'
                        }}
                        title="Refresh logs"
                    >
                        <Clock size={16} className={loading ? 'animate-spin' : ''} />
                    </button>
                </div>
            </div>

            <div style={{
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border-color)',
                borderRadius: 12,
                overflow: 'hidden',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
            }}>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                        <thead>
                            <tr style={{ background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-color)' }}>
                                <th style={{ padding: '12px 24px', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Event</th>
                                <th style={{ padding: '12px 24px', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>User ID</th>
                                <th style={{ padding: '12px 24px', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Timestamp</th>
                                <th style={{ padding: '12px 24px', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right' }}>Details</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && logs.length === 0 ? (
                                <tr>
                                    <td colSpan={4} style={{ padding: '48px 24px', textAlign: 'center' }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                                            <div style={{ width: 24, height: 24, border: '2px solid var(--accent-primary)', borderTopColor: 'transparent', borderRadius: '50%' }} className="animate-spin" />
                                            <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading security events...</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : filteredLogs.length === 0 ? (
                                <tr>
                                    <td colSpan={4} style={{ padding: '48px 24px', textAlign: 'center', color: 'var(--text-muted)' }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                                            <Search size={32} style={{ opacity: 0.2, marginBottom: 8 }} />
                                            <p style={{ fontSize: 14 }}>No audit logs found matching your criteria</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                filteredLogs.map((log) => (
                                    <tr
                                        key={log.id}
                                        style={{ borderBottom: '1px solid var(--border-color)', cursor: 'pointer' }}
                                        onClick={() => setSelectedLog(log)}
                                        className="hover-row"
                                    >
                                        <td style={{ padding: '16px 24px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                <div style={{ padding: 6, background: 'var(--bg-tertiary)', borderRadius: 6, display: 'flex' }}>
                                                    {getEventIcon(log.type)}
                                                </div>
                                                <span style={{ fontWeight: 500, color: 'var(--text-primary)', textTransform: 'capitalize' }}>
                                                    {log.type.replace(/_/g, ' ')}
                                                </span>
                                            </div>
                                        </td>
                                        <td style={{ padding: '16px 24px', fontSize: 13, color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                                            {log.user_id || 'System'}
                                        </td>
                                        <td style={{ padding: '16px 24px', fontSize: 13, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                                            {new Date(log.timestamp).toLocaleString()}
                                        </td>
                                        <td style={{ padding: '16px 24px', textAlign: 'right' }}>
                                            <button style={{
                                                background: 'transparent',
                                                border: 'none',
                                                color: 'var(--accent-primary)',
                                                fontSize: 12,
                                                fontWeight: 500,
                                                cursor: 'pointer'
                                            }}>
                                                View JSON
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <div style={{ padding: '12px 24px', background: 'var(--bg-tertiary)', borderTop: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <p style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                        Showing {filteredLogs.length} of {logs.length} recent logs
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <select
                            value={limit}
                            onChange={(e) => setLimit(Number(e.target.value))}
                            style={{
                                fontSize: 11,
                                background: 'var(--bg-secondary)',
                                border: '1px solid var(--border-color)',
                                borderRadius: 4,
                                padding: '2px 8px',
                                color: 'var(--text-primary)',
                                outline: 'none'
                            }}
                        >
                            <option value={50}>50 rows</option>
                            <option value={100}>100 rows</option>
                            <option value={500}>500 rows</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Details Modal */}
            {selectedLog && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 100,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 16,
                        background: 'rgba(0,0,0,0.4)',
                        backdropFilter: 'blur(4px)'
                    }}
                    onClick={() => setSelectedLog(null)}
                >
                    <div
                        style={{
                            background: 'var(--bg-primary)',
                            border: '1px solid var(--border-color)',
                            borderRadius: 16,
                            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
                            width: '100%',
                            maxWidth: 600,
                            overflow: 'hidden'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                {getEventIcon(selectedLog.type)}
                                <h3 style={{ fontWeight: 700, color: 'var(--text-primary)', textTransform: 'capitalize' }}>
                                    {selectedLog.type.replace(/_/g, ' ')}
                                </h3>
                            </div>
                            <button
                                onClick={() => setSelectedLog(null)}
                                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}
                            >
                                <ChevronRight size={20} style={{ transform: 'rotate(90deg)' }} />
                            </button>
                        </div>

                        <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    <span style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <User size={12} /> User ID
                                    </span>
                                    <p style={{ fontSize: 13, fontFamily: 'monospace', background: 'var(--bg-tertiary)', padding: 8, borderRadius: 6, border: '1px solid var(--border-color)' }}>
                                        {selectedLog.user_id || 'System'}
                                    </p>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    <span style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <Calendar size={12} /> Timestamp
                                    </span>
                                    <p style={{ fontSize: 13, background: 'var(--bg-tertiary)', padding: 8, borderRadius: 6, border: '1px solid var(--border-color)' }}>
                                        {new Date(selectedLog.timestamp).toLocaleString()}
                                    </p>
                                </div>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                <span style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <Info size={12} /> Raw Event Details
                                </span>
                                <pre style={{
                                    fontSize: 11,
                                    fontFamily: 'monospace',
                                    background: '#1a1a1a',
                                    color: '#36c5f0',
                                    padding: 16,
                                    borderRadius: 12,
                                    overflowX: 'auto',
                                    maxHeight: 250,
                                    margin: 0
                                }}>
                                    {formatDetails(selectedLog.details)}
                                </pre>
                            </div>
                        </div>

                        <div style={{ padding: '16px 24px', background: 'var(--bg-tertiary)', borderTop: '1px solid var(--border-color)', display: 'flex', justifyContent: 'end' }}>
                            <button
                                onClick={() => setSelectedLog(null)}
                                style={{
                                    padding: '8px 16px',
                                    background: 'var(--accent-primary)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: 8,
                                    fontSize: 13,
                                    fontWeight: 600,
                                    cursor: 'pointer'
                                }}
                            >
                                Close Details
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export { };
